import React from 'react';
import { StyleSheet, Pressable } from 'react-native';
import { XStack, YStack } from '@tamagui/stacks';
import { Text } from '@tamagui/core';
import { BadgeCheck } from 'lucide-react-native';
import { COLORS } from '@/constants/colors';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';

type ChatListItemProps = {
  imageUri: string;
  name: string;
  lastMessage: string;
  time: string;
  unreadCount?: number;
  verified?: boolean;
  onPress: () => void;
  isDark?: boolean;
  selected?: boolean;
};

const AVATAR_SIZE = 56;

const ChatListItem = React.memo<ChatListItemProps>(({
  imageUri,
  name,
  lastMessage,
  time,
  unreadCount = 0,
  verified = false,
  onPress,
  isDark,
  selected = false,
}) => {
  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
  const subtextColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;
  // Don't show unread badge if chat is currently selected/active
  const hasUnread = unreadCount > 0 && !selected;
  const backgroundColor = selected 
    ? (isDark ? 'rgba(94, 128, 156, 0.2)' : 'rgba(94, 128, 156, 0.1)')
    : 'transparent';

  return (
    <Pressable onPress={onPress}>
      <XStack
        alignItems="center"
        gap="$3"
        paddingVertical="$3"
        paddingHorizontal="$4"
        backgroundColor={backgroundColor}
        marginBottom="$2"
      >
        {/* Avatar */}
        {imageUri ? (
          <AuthenticatedImage
            uri={imageUri}
            style={styles.avatar}
            resizeMode="cover"
          />
        ) : (
          <YStack style={[styles.avatar, styles.avatarPlaceholder]} justifyContent="center" alignItems="center">
            <Text color={COLORS.WHITE} fontSize={20} fontWeight="600">
              {name.charAt(0).toUpperCase()}
            </Text>
          </YStack>
        )}

        {/* Content */}
        <YStack flex={1} gap="$1">
          {/* Name and Time */}
          <XStack justifyContent="space-between" alignItems="center">
            <XStack alignItems="center" gap="$1">
              <Text
                fontSize={16}
                fontWeight={hasUnread ? '700' : '600'}
                color={textColor}
              >
                {name}
              </Text>
              {verified && (
                <BadgeCheck size={16} color={COLORS.WHITE} fill={COLORS.SUCCESS} />
              )}
            </XStack>
            <Text fontSize={12} color={subtextColor}>
              {time}
            </Text>
          </XStack>

          {/* Last Message and Badge */}
          <XStack justifyContent="space-between" alignItems="center">
            <Text
              fontSize={14}
              color={subtextColor}
              numberOfLines={1}
              flex={1}
              fontWeight={hasUnread ? '600' : '400'}
            >
              {lastMessage}
            </Text>
            {hasUnread && (
              <YStack style={styles.badge}>
                <Text fontSize={11} color={COLORS.WHITE} fontWeight="700">
                  {unreadCount}
                </Text>
              </YStack>
            )}
          </XStack>
        </YStack>
      </XStack>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
  },
  avatarPlaceholder: {
    backgroundColor: COLORS.PRIMARY,
  },
  badge: {
    backgroundColor: COLORS.ERROR,
    borderRadius: 12,
    minWidth: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    marginLeft: 8,
  },
});

ChatListItem.displayName = 'ChatListItem';

export default ChatListItem;

